import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Clock, ChevronRight } from "lucide-react";
import { useSEO } from "@/lib/seo";
import api from "@/lib/api";

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Saint Lucia Travel Blog - Guides, Tips & Things to Do",
    description: "Expert Saint Lucia travel guides, adventure tips, best time to visit, what to pack, and insider recommendations from Keyz's Epic Adventure Tours. Plan your perfect Caribbean holiday.",
  });

  useEffect(() => {
    api.get("/blog").then(r => { setPosts(Array.isArray(r.data) ? r.data : []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      <div className="pt-28 pb-24 px-6 max-w-5xl mx-auto">
        <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Stories & Guides</p>
        <h1 className="font-cormorant text-4xl sm:text-5xl font-light text-midnight mb-4" data-testid="blog-title">
          Saint Lucia Travel Blog
        </h1>
        <p className="font-manrope text-sm sm:text-base text-midnight/50 max-w-xl mb-12">
          Insider tips, adventure guides, and everything you need to plan your dream Caribbean getaway.
        </p>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1,2,3,4].map(i => (
              <div key={i} className="animate-pulse">
                <div className="bg-midnight/5 aspect-[16/10] mb-4" />
                <div className="h-3 bg-midnight/5 w-20 mb-2" />
                <div className="h-5 bg-midnight/5 w-3/4 mb-2" />
                <div className="h-3 bg-midnight/5 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-grid">
            {posts.map((post, i) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className={`group block ${i === 0 ? 'md:col-span-2' : ''}`}
                data-testid={`blog-card-${post.slug}`}
              >
                <div className={`overflow-hidden ${i === 0 ? 'aspect-[21/9]' : 'aspect-[16/10]'} mb-4`}>
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <Badge variant="outline" className="font-manrope text-[10px] uppercase tracking-wider border-sandy/30 text-sandy px-2 py-0">
                    {post.category}
                  </Badge>
                  <span className="flex items-center gap-1 font-manrope text-[10px] text-midnight/30">
                    <Clock className="w-2.5 h-2.5" /> {post.read_time}
                  </span>
                </div>
                <h2 className={`font-cormorant ${i === 0 ? 'text-2xl sm:text-3xl' : 'text-xl'} text-midnight mb-2 leading-tight group-hover:text-sandy transition-colors duration-300`}>
                  {post.title}
                </h2>
                <p className="font-manrope text-sm text-midnight/50 leading-relaxed line-clamp-2">
                  {post.excerpt}
                </p>
                <span className="inline-flex items-center gap-1 mt-3 text-sandy font-manrope text-xs group-hover:gap-2 transition-all duration-300">
                  Read More <ChevronRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
